import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const LOGO = '/dailyreps3.png';
const API_BASE = process.env.REACT_APP_API_URL || '';

export default function UniversalPlayerLogin() {
  const navigate = useNavigate();
  const { loginPlayer, completePlayerLogin } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Password change state
  const [changeSession, setChangeSession] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changeError, setChangeError] = useState('');
  const [changeSaving, setChangeSaving] = useState(false);

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotIdentifier, setForgotIdentifier] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotResult, setForgotResult] = useState(null);
  const [forgotError, setForgotError] = useState('');

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: forgotIdentifier, type: 'player' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong');
      setForgotResult('sent');
    } catch (err) {
      setForgotError(err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await loginPlayer(username, password);
      if (result.must_change_password) {
        setChangeSession(result.change_session);
      } else {
        navigate('/player');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangeError('');
    if (newPassword.length < 6) {
      setChangeError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangeError('Passwords do not match.');
      return;
    }
    setChangeSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/player-change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ change_session: changeSession, new_password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to change password');
      completePlayerLogin(data);
      navigate('/player');
    } catch (err) {
      setChangeError(err.message);
    } finally {
      setChangeSaving(false);
    }
  };

  // Password change screen
  if (changeSession) {
    return (
      <div className="login-page">
        <img src={LOGO} alt="Train Daily" className="login-logo" />
        <h1 className="login-title">
          <span>Train Daily</span> Player Login
        </h1>
        <div style={{ textAlign: 'center', marginBottom: 16, fontSize: '0.95rem', color: '#666' }}>
          Please choose a new password to continue.
        </div>
        <form className="login-form" onSubmit={handleChangePassword}>
          {changeError && <div className="login-error">{changeError}</div>}
          <input
            type="password"
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={6}
          />
          <input
            type="password"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={6}
          />
          <button className="btn btn-orange" type="submit" disabled={changeSaving}>
            {changeSaving ? 'Saving...' : 'Set Password'}
          </button>
        </form>
      </div>
    );
  }

  if (forgotMode) {
    return (
      <div className="login-page">
        <img src={LOGO} alt="Train Daily" className="login-logo" />
        <h1 className="login-title">
          <span>Train Daily</span> Player Login
        </h1>
        {forgotResult === 'sent' && (
          <div style={{ textAlign: 'center', maxWidth: 360 }}>
            <p style={{ color: '#fff', fontSize: '0.95rem', lineHeight: 1.5 }}>
              If an account was found, a password reset link has been sent. Check your email.
            </p>
            <button
              onClick={() => { setForgotMode(false); setForgotResult(null); setForgotIdentifier(''); }}
              style={{ background: 'none', border: 'none', color: '#fff', textDecoration: 'underline', cursor: 'pointer', marginTop: 12, fontSize: '0.9rem' }}
            >
              Back to Sign In
            </button>
          </div>
        )}
        {!forgotResult && (
          <>
            <p style={{ color: '#fff', fontSize: '0.9rem', textAlign: 'center', marginBottom: 16 }}>
              Enter your username or email to receive a password reset link.
            </p>
            <form className="login-form" onSubmit={handleForgotSubmit}>
              {forgotError && <div className="login-error">{forgotError}</div>}
              <input
                type="text"
                placeholder="Username or Email"
                value={forgotIdentifier}
                onChange={(e) => setForgotIdentifier(e.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
                required
              />
              <button className="btn btn-orange" type="submit" disabled={forgotLoading}>
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
            <button
              onClick={() => { setForgotMode(false); setForgotError(''); setForgotIdentifier(''); }}
              style={{ background: 'none', border: 'none', color: '#fff', textDecoration: 'underline', cursor: 'pointer', marginTop: 20, fontSize: '0.9rem' }}
            >
              Back to Sign In
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="login-page">
      <img src={LOGO} alt="Train Daily" className="login-logo" />
      <h1 className="login-title">
        <span>Train Daily</span> Player Login
      </h1>
      <form className="login-form" onSubmit={handleSubmit}>
        {error && <div className="login-error">{error}</div>}
        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoCapitalize="none"
          autoCorrect="off"
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button className="btn btn-orange" type="submit" disabled={loading}>
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
      <button
        onClick={() => setForgotMode(true)}
        style={{ background: 'none', border: 'none', color: '#fff', textDecoration: 'underline', cursor: 'pointer', marginTop: 20, fontSize: '0.9rem' }}
      >
        Forgot Password?
      </button>
    </div>
  );
}
